import "./App.css";

import { Link } from "react-router-dom";
import { useState } from "react";
import { WEB_APP_URL } from "./config";

function TrainingProviders() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const partnershipEmail =
    "mailto:contact@fit-match.co.uk?subject=Training%20Provider%20Partnership";

  return (
    <div className="bg-black text-gray-200">
      {/* Navbar */}
      <header className="bg-black bg-opacity-70 backdrop-blur-lg sticky top-0 w-full z-50 shadow-lg">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Fit-Match Logo"
              className="h-16 w-auto"
            />
            <Link to="/" className="text-2xl font-bold gradient-text">
              FIT-MATCH
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#how-it-works" className="nav-link font-medium">
              How It Works
            </Link>
            <Link to="/our-story" className="nav-link font-medium">
              Our Story
            </Link>
            <Link to="/professionals" className="nav-link font-medium">
              Pros
            </Link>
            <Link to="/training-providers" className="nav-link font-medium">
              Providers
            </Link>
            <Link to="/communities" className="nav-link font-medium">
              Communities
            </Link>
            <Link to="/facilities" className="nav-link font-medium">
              Facilities
            </Link>
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Login
            </a>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-4 6h4"
                ></path>
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:hidden bg-black bg-opacity-90 py-4`}
        >
          <Link
            to="/#how-it-works"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            How It Works
          </Link>
          <Link
            to="/our-story"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Our Story
          </Link>
          <Link
            to="/professionals"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Pros
          </Link>
          <Link
            to="/training-providers"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Providers
          </Link>
          <Link
            to="/communities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Communities
          </Link>
          <Link
            to="/facilities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Facilities
          </Link>
          <a
            href={WEB_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Login
          </a>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero: high-level partnership pitch */}
        <section className="hero-bg py-20 md:py-32 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight">
              Give Your{" "}
              <span className="gradient-text">Graduates a Head Start</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto">
              Partner with Fit-Match to give your newly qualified professionals{" "}
              <span className="font-semibold">6 months of full membership</span>{" "}
              at no cost, and turn every cohort into a visible, connected
              community on our platform.
            </p>
            <div className="mt-8 flex flex-col items-center gap-4 md:flex-row md:justify-center">
              <a
                href={partnershipEmail}
                className="bg-blue-fit-match text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-blue-600 transition duration-300"
              >
                Contact us about partnership
              </a>
              <a
                href="#how-it-works"
                className="text-sm md:text-base text-gray-300 hover:text-white underline-offset-4 hover:underline"
              >
                See how the partnership works
              </a>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-16 bg-gray-950">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              A Zero-Cost Partnership With Real Graduate Value
            </h2>
            <p className="text-lg text-gray-300 mb-4">
              Fit-Match partners with training academies and course providers to
              give their students and graduates{" "}
              <span className="font-semibold">
                6 months of full professional membership
              </span>{" "}
              on the platform, completely free.
            </p>
            <p className="text-lg text-gray-300 mb-4">
              That is double the standard 3-month free period offered to all new
              professionals, reserved only for graduates who come through your
              programmes.
            </p>
          </div>
        </section>

        {/* Benefits for training providers */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Why Partner With Fit-Match?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">Zero Cost</h3>
                <p className="text-gray-400 text-sm">
                  There is no fee, contract, or long-term commitment required.
                  You can offer this benefit to your students at absolutely no
                  cost to your organisation.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">
                  You Control Your Account
                </h3>
                <p className="text-gray-400 text-sm">
                  Training providers are invited to sign up and create their own
                  Fit-Match account. You manage your profile yourself — images,
                  services, bio, and details — without relying on us to update it
                  for you.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">
                  Genuine Graduate Benefit
                </h3>
                <p className="text-gray-400 text-sm">
                  Give your students a tangible perk as part of their
                  qualification — a professional online presence and a place to
                  start building their client base from day one.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">
                  Credibility by Association
                </h3>
                <p className="text-gray-400 text-sm">
                  Being linked to a growing fitness discovery platform adds
                  credibility to your programme and shows you invest in your
                  graduates&apos; careers beyond certification.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">
                  Your Own Fit-Match Profile
                </h3>
                <p className="text-gray-400 text-sm">
                  You receive a full provider profile on Fit-Match — with
                  images, bio, services, reviews, and messaging — discoverable
                  just like any professional or facility.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">On-Map Presence</h3>
                <p className="text-gray-400 text-sm">
                  Your academy is pinned on the Fit-Match map and fully
                  searchable by both clients and professionals in your area.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">Inbound Enquiries</h3>
                <p className="text-gray-400 text-sm">
                  Anyone can message you directly through the app — prospective
                  students, partners, and professionals — creating a single
                  channel for inbound interest.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">Graduate Showcase</h3>
                <p className="text-gray-400 text-sm">
                  Every professional who signs up via your link is listed on
                  your profile, creating a living alumni wall that grows with
                  each cohort and showcases your impact.
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold mb-2">
                  One-Stop-Shop for Students
                </h3>
                <p className="text-gray-400 text-sm">
                  Instead of graduates scrambling to build their online
                  presence, Fit-Match gives them everything in one place: map
                  visibility, profile, messaging, and reviews.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What graduates get */}
        <section className="py-16 bg-gray-950">
          <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              What Your Graduates Get – 6 Months Free
            </h2>
            <p className="text-center text-gray-300 mb-10 max-w-3xl mx-auto">
              Every graduate who signs up via your unique link receives{" "}
              <span className="font-semibold">6 months of full professional</span>{" "}
              membership on Fit-Match, including all the tools they need to
              launch and grow their career.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  Discovery & Visibility
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>Featured on the Fit-Match map by location and specialism.</li>
                  <li>
                    A public, premium profile that can be shared anywhere online.
                  </li>
                  <li>Appears in local search results where clients are looking.</li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  Fit-Match ID – Digital Business Card
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>
                    A shareable profile link (for example:{" "}
                    <span className="text-blue-fit-match">
                      fit-match.co.uk/john-smith
                    </span>
                    ).
                  </li>
                  <li>QR code for instant in-person networking.</li>
                  <li>
                    Digital wallet card for Apple Wallet / Google Wallet, so
                    their details are always one tap away.
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  Reviews & Social Proof
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>
                    Clients can leave reviews directly on the professional&apos;s
                    profile.
                  </li>
                  <li>
                    No app download needed for clients — lower friction means
                    more feedback.
                  </li>
                  <li>
                    Helps a brand-new professional build credibility faster.
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">Messaging</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>Direct client enquiries through Fit-Match.</li>
                  <li>No need to juggle third-party tools or scattered inboxes.</li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">Profile Features</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>Images and media to showcase their work and environment.</li>
                  <li>Editable bio and specialisms that evolve with their career.</li>
                  <li>Custom buttons and links for additional services or offers.</li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  Web Access – No App Required for Clients
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>
                    All profiles are fully viewable on the web as well as in the
                    app.
                  </li>
                  <li>
                    Clients can discover, review, and contact professionals
                    without any extra friction.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Training provider profile details */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-6 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Your Presence on Fit-Match
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  Account & Profile Management
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>
                    You&apos;re invited to create a Fit-Match account just like a
                    professional would, giving you full access to provider tools.
                  </li>
                  <li>
                    Log in to update images, edit your bio, add or change services,
                    and respond to reviews whenever you like.
                  </li>
                  <li>
                    No middleman – you own and control your presence on the
                    platform.
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">On the Map</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>Your organisation is pinned at your location.</li>
                  <li>
                    Searchable by both clients and fitness professionals looking
                    for training.
                  </li>
                  <li>
                    Appears alongside gyms, studios, and professionals in your
                    area.
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  On Your Profile Page
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>Name, logo, and cover imagery that reflects your brand.</li>
                  <li>
                    Bio and about section to describe your courses, ethos, and
                    specialisms.
                  </li>
                  <li>
                    Services listed — PT courses, nutrition qualifications,
                    specialist modules and more.
                  </li>
                  <li>
                    Reviews from clients and graduates, plus direct messaging via
                    the app.
                  </li>
                  <li>
                    A dedicated graduates section that showcases everyone who
                    trained with you and is now on Fit-Match.
                  </li>
                </ul>
              </div>

              <div className="bg-gray-900 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-2">
                  On Every Graduate&apos;s Profile
                </h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>
                    A clear &quot;Trained at [Provider Name]&quot; section on the
                    professional&apos;s profile.
                  </li>
                  <li>Links back to your Fit-Match provider page.</li>
                  <li>
                    Every time a graduate is discovered, your brand appears
                    alongside them.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How it works flow */}
        <section id="how-it-works" className="py-16 bg-gray-950">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              How the Partnership Flow Works
            </h2>
            <ol className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    Unique referral link created for your academy
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Fit-Match sets up a dedicated sign-up link for your training
                    provider that tracks which graduates came through you.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    You share the link with students
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Include it in your onboarding or course completion packs so
                    students know Fit-Match is part of their qualification.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    Graduates sign up via your link
                  </h3>
                  <p className="text-gray-400 text-sm">
                    When they complete sign-up, they automatically receive{" "}
                    <span className="font-semibold">6 months of full access</span>{" "}
                    instead of the standard 3.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    Training provider affiliation shown on profiles
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Each graduate&apos;s profile clearly shows that they trained
                    with you, reinforcing the quality of your programme.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    You appear on the map and in search
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Your academy profile is live on Fit-Match with messaging,
                    reviews, and your graduates listed in one place.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-blue-fit-match flex items-center justify-center font-bold text-black">
                    6
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">
                    Evergreen pipeline with every new cohort
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Each graduating class joins the platform through your link,
                    creating a steady flow of professionals associated with your
                    brand.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Next steps / CTA */}
        <section className="py-16 bg-gray-950">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Talk About Partnership?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              If you&apos;re interested in offering Fit-Match as a graduate
              benefit — or you have questions about how it would work for your
              academy — we&apos;d love to speak with you.
            </p>
            <a
              href={partnershipEmail}
              className="bg-blue-fit-match text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-blue-600 transition duration-300"
            >
              Contact us about partnership
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2024 Fit-Match. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TrainingProviders;
